import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Network } from 'vis-network';
import { DataSet } from 'vis-data';
import { Neo4jSchema, NodeDefinition, SchemaEvaluation } from '../../types';
import { Card } from '../ui/Card';
import Button from '../ui/Button';
import { Plus, Trash2, Edit2, Save, X, GitBranch, ZoomIn, ZoomOut, Maximize2, Minimize2, Move, Grid3X3, Download, Search } from 'lucide-react';
import { AddNodeDialog } from './AddNodeDialog';
import { AddRelationshipDialog } from './AddRelationshipDialog';
import { SchemaLegend } from '../knowledge-graph-design/SchemaLegend';
import { getNodeColors, EDGE_STYLE } from '../../lib/graph/graphTheme';
import { exportCanvasToPng } from '../../lib/graph/graphExport';

interface SchemaVisualizerProps {
  schema: Neo4jSchema;
  evaluation?: SchemaEvaluation;
  onSchemaUpdate?: (updatedSchema: Neo4jSchema) => void;
}

interface VisNode {
  id: string;
  label: string;
  title: string;
  color?: {
    background: string;
    border: string;
    highlight?: {
      background: string;
      border: string;
    };
  };
  borderWidth?: number;
  borderDashes?: boolean | number[];
  font?: {
    size: number;
    color?: string;
  };
}

interface VisEdge {
  id: string;
  from: string;
  to: string;
  label: string;
  title: string;
  arrows: string;
  color?: {
    color: string;
    highlight?: string;
  };
  dashes?: boolean | number[];
}

const PROPERTY_TYPES = [
  'String',
  'Integer',
  'Float',
  'Boolean',
  'DateTime',
  'Date',
  'Time',
  'LocalDateTime',
  'LocalTime',
  'Duration',
  'Point',
  'List<String>',
  'List<Integer>'
];

export const SchemaVisualizer: React.FC<SchemaVisualizerProps> = ({
  schema,
  evaluation,
  onSchemaUpdate
}) => {
  const networkContainer = useRef<HTMLDivElement>(null);
  const networkRef = useRef<Network | null>(null);
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [selectedEdge, setSelectedEdge] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedSchema, setEditedSchema] = useState<Neo4jSchema>(schema);

  const [showAddNodeDialog, setShowAddNodeDialog] = useState(false);
  const [showAddRelationshipDialog, setShowAddRelationshipDialog] = useState(false);
  const [isEditingProperties, setIsEditingProperties] = useState(false);
  const [editedNodeData, setEditedNodeData] = useState<any>(null);
  const [editedEdgeData, setEditedEdgeData] = useState<any>(null);

  // Layout and view controls
  const [layoutMode, setLayoutMode] = useState<'hierarchical' | 'freeform'>('hierarchical');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const fullscreenRef = useRef<HTMLDivElement>(null);

  // KG areas present in the schema (drives the shared color legend).
  const usedAreas = useMemo(() => {
    const set = new Set<string>();
    schema.nodes?.forEach((n) => {
      if (n.category) set.add(n.category);
    });
    return Array.from(set);
  }, [schema]);

  // Zoom controls
  const handleZoomIn = useCallback(() => {
    if (networkRef.current) {
      const scale = networkRef.current.getScale();
      networkRef.current.moveTo({ scale: scale * 1.3 });
    }
  }, []);

  const handleZoomOut = useCallback(() => {
    if (networkRef.current) {
      const scale = networkRef.current.getScale();
      networkRef.current.moveTo({ scale: scale / 1.3 });
    }
  }, []);

  const handleFitToScreen = useCallback(() => {
    if (networkRef.current) {
      networkRef.current.fit({
        animation: {
          duration: 500,
          easingFunction: 'easeInOutQuad'
        }
      });
    }
  }, []);

  // Fullscreen toggle
  const handleToggleFullscreen = useCallback(() => {
    if (!fullscreenRef.current) return;

    if (!isFullscreen) {
      if (fullscreenRef.current.requestFullscreen) {
        fullscreenRef.current.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  }, [isFullscreen]);

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Toggle layout mode
  const handleToggleLayoutMode = useCallback(() => {
    setLayoutMode(prev => prev === 'hierarchical' ? 'freeform' : 'hierarchical');
  }, []);

  // Locate a node by label: select + focus it.
  const handleSearch = useCallback(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q || !networkRef.current) return;
    const match = schema.nodes?.find((n) => n.label.toLowerCase().includes(q));
    if (match) {
      networkRef.current.selectNodes([match.label]);
      setSelectedNode(match.label);
      setSelectedEdge(null);
      networkRef.current.focus(match.label, {
        scale: 1.2,
        animation: { duration: 500, easingFunction: 'easeInOutQuad' },
      });
    }
  }, [searchQuery, schema]);

  // Export the current graph canvas as PNG.
  const handleExportPng = useCallback(() => {
    const canvas = networkContainer.current?.querySelector('canvas') as HTMLCanvasElement | null;
    exportCanvasToPng(canvas, 'schema-graph.png');
  }, []);

  /**
   * Step 2: Transform SchemaDesign JSON to Vis.js Format
   *
   * This function converts our Neo4j schema JSON to the format expected by Vis.js:
   * - nodes: Array of { id, label, title, color, ... }
   * - edges: Array of { id, from, to, label, arrows, ... }
   */
  const transformSchemaToVisFormat = (): { nodes: VisNode[], edges: VisEdge[] } => {
    const visNodes: VisNode[] = [];
    const visEdges: VisEdge[] = [];

    // Get missing elements from evaluation if available
    const missingNodes = new Set<string>();
    const missingRelationships = new Set<string>();

    if (evaluation) {
      evaluation.executiveQuestionsAnalysis.findings.forEach(finding => {
        finding.missingElements?.forEach(element => {
          // Simple heuristic: if it contains spaces or "relationship", it's likely a relationship
          if (element.toLowerCase().includes('relationship') || element.includes('→')) {
            missingRelationships.add(element);
          } else {
            missingNodes.add(element);
          }
        });
      });
    }

    // Degree map — used to give well-connected "hub" nodes a thicker border.
    const degree: Record<string, number> = {};
    schema.relationships?.forEach((rel: any) => {
      const s = rel.from || rel.sourceLabel;
      const t = rel.to || rel.targetLabel;
      if (s) degree[s] = (degree[s] || 0) + 1;
      if (t) degree[t] = (degree[t] || 0) + 1;
    });
    const maxDegree = Math.max(1, ...Object.values(degree));

    // Transform nodes from schema.nodes
    schema.nodes?.forEach((node: NodeDefinition) => {
      // Handle both array and object properties format
      let propertyCount = 0;
      if (node.properties) {
        if (Array.isArray(node.properties)) {
          propertyCount = node.properties.length;
        } else if (typeof node.properties === 'object') {
          propertyCount = Object.keys(node.properties).length;
        }
      }

      const hasConstraint = schema.constraints?.some(c => c.label === node.label);
      const hasIndex = schema.indexes?.some(i => i.label === node.label);

      // Check if this node is mentioned as missing in evaluation
      const isMissing = missingNodes.has(node.label);
      // Category-based colors from the shared theme (falls back to `default`).
      const colors = getNodeColors(node.category);
      const isHub = maxDegree > 1 && (degree[node.label] || 0) >= maxDegree * 0.6;

      visNodes.push({
        id: node.label,
        label: `${node.label}\n(${propertyCount} props)`,
        title: `
          <b>${node.label}</b><br/>
          Properties: ${propertyCount}<br/>
          Constraints: ${hasConstraint ? '✓' : '✗'}<br/>
          Indexes: ${hasIndex ? '✓' : '✗'}<br/>
          ${node.description || ''}
        `,
        color: {
          background: isMissing ? '#ffebee' : colors.bg,
          border: isMissing ? '#ef5350' : colors.border,
          highlight: {
            background: isMissing ? '#ffcdd2' : colors.bg,
            border: isMissing ? '#d32f2f' : colors.border
          }
        },
        borderWidth: isMissing ? 3 : (isHub ? 3 : 2),
        borderDashes: isMissing ? [5, 5] : false,
        font: {
          size: 14,
          color: colors.text
        }
      });
    });

    // Transform relationships from schema.relationships
    schema.relationships?.forEach((rel: any) => {
      // Handle both array and object properties format
      let propertyCount = 0;
      if (rel.properties) {
        if (Array.isArray(rel.properties)) {
          propertyCount = rel.properties.length;
        } else if (typeof rel.properties === 'object') {
          propertyCount = Object.keys(rel.properties).length;
        }
      }

      // Support both backend format (from/to) and frontend format (sourceLabel/targetLabel)
      const sourceLabel = rel.from || rel.sourceLabel;
      const targetLabel = rel.to || rel.targetLabel;
      const relationshipId = `${sourceLabel}_${rel.type}_${targetLabel}`;

      // Check if this relationship type is mentioned as missing
      const isMissing = Array.from(missingRelationships).some(
        missing => missing.includes(rel.type)
      );

      visEdges.push({
        id: relationshipId,
        from: sourceLabel,
        to: targetLabel,
        label: rel.type,
        title: `
          <b>${rel.type}</b><br/>
          From: ${sourceLabel}<br/>
          To: ${targetLabel}<br/>
          Properties: ${propertyCount}<br/>
          ${rel.description || ''}
        `,
        arrows: 'to',
        color: {
          color: isMissing ? EDGE_STYLE.missing : EDGE_STYLE.base,
          highlight: isMissing ? '#f57c00' : EDGE_STYLE.highlight
        },
        dashes: isMissing ? [5, 5] : false
      });
    });

    // Dedupe by id — guards against duplicate labels / relationships that would
    // otherwise throw when added to the vis DataSet.
    const uniqueNodes = Array.from(new Map(visNodes.map(n => [n.id, n])).values());
    const uniqueEdges = Array.from(new Map(visEdges.map(e => [e.id, e])).values());
    return { nodes: uniqueNodes, edges: uniqueEdges };
  };

  /**
   * Initialize Vis.js Network
   */
  useEffect(() => {
    if (!networkContainer.current) return;

    const { nodes, edges } = transformSchemaToVisFormat();

    // Create DataSets for dynamic updates
    const nodesDataSet = new DataSet(nodes);
    const edgesDataSet = new DataSet(edges);

    // Vis.js network options - dynamic based on layout mode
    const options = {
      nodes: {
        shape: 'box',
        margin: { top: 10, right: 10, bottom: 10, left: 10 },
        widthConstraint: {
          minimum: 100,
          maximum: 200
        },
        font: {
          size: 14,
          face: 'Inter, system-ui, sans-serif'
        }
      },
      edges: {
        width: 2,
        smooth: {
          enabled: true,
          type: layoutMode === 'hierarchical' ? 'cubicBezier' : 'dynamic',
          // Must follow the hierarchical direction (now 'UD'), else the curves fight
          // the layout and edges read as tangled diagonals.
          forceDirection: layoutMode === 'hierarchical' ? 'vertical' : undefined,
          roundness: 0.4
        },
        font: {
          size: 12,
          align: 'middle',
          background: '#ffffff',
          strokeWidth: 3,
          strokeColor: '#ffffff'
        }
      },
      layout: layoutMode === 'hierarchical' ? {
        hierarchical: {
          enabled: true,
          // Top-down: each level becomes a horizontal ROW, so same-level nodes spread
          // across the canvas's full width. With 'LR' the levels were columns and every
          // same-level node stacked vertically — which squeezed the whole schema into a
          // narrow strip down the middle of a wide (landscape) canvas.
          direction: 'UD',
          sortMethod: 'directed',
          shakeTowards: 'roots',
          levelSeparation: 180, // vertical gap between rows (vertical space is scarce)
          nodeSpacing: 180,     // horizontal gap between siblings within a row
          treeSpacing: 220      // gap between disconnected sub-graphs
        }
      } : {
        hierarchical: {
          enabled: false
        },
        improvedLayout: true,
        randomSeed: 42 // Consistent initial layout
      },
      physics: layoutMode === 'freeform' ? {
        enabled: true,
        stabilization: {
          enabled: true,
          iterations: 100,
          updateInterval: 25
        },
        barnesHut: {
          gravitationalConstant: -3000,
          centralGravity: 0.3,
          springLength: 200,
          springConstant: 0.04,
          damping: 0.09
        }
      } : {
        enabled: false // Disable physics for hierarchical layout
      },
      interaction: {
        hover: true,
        tooltipDelay: 200,
        zoomView: true,
        dragView: true,
        dragNodes: true, // Enable node dragging
        navigationButtons: false, // We'll use custom buttons
        keyboard: {
          enabled: true,
          speed: { x: 10, y: 10, zoom: 0.1 }
        }
      }
    };

    // Create network
    const network = new Network(
      networkContainer.current,
      { nodes: nodesDataSet, edges: edgesDataSet },
      options
    );

    // Event listeners
    network.on('selectNode', (params) => {
      if (params.nodes.length > 0) {
        setSelectedNode(params.nodes[0]);
        setSelectedEdge(null);
      }
    });

    network.on('selectEdge', (params) => {
      // Only select edge if NO nodes are selected (to avoid conflict)
      if (params.edges.length > 0 && params.nodes.length === 0) {
        setSelectedEdge(params.edges[0]);
        setSelectedNode(null);
      }
    });

    network.on('deselectNode', () => {
      setSelectedNode(null);
    });

    network.on('deselectEdge', () => {
      setSelectedEdge(null);
    });

    // Hover neighbor-highlight: fade nodes/edges not connected to the hovered node.
    network.on('hoverNode', (params: any) => {
      const id = params.node as string;
      const connectedNodes = new Set<string>([
        id,
        ...(network.getConnectedNodes(id) as unknown[]).map(String),
      ]);
      const connectedEdges = new Set<string>(
        (network.getConnectedEdges(id) as unknown[]).map(String)
      );
      nodesDataSet.forEach((n: any) => {
        nodesDataSet.update({ id: n.id, opacity: connectedNodes.has(String(n.id)) ? 1 : 0.2 } as any);
      });
      edges.forEach((e) => {
        edgesDataSet.update({
          id: e.id,
          color: { ...(e.color || {}), opacity: connectedEdges.has(e.id) ? 1 : 0.08 },
        } as any);
      });
    });

    network.on('blurNode', () => {
      nodesDataSet.forEach((n: any) => nodesDataSet.update({ id: n.id, opacity: 1 } as any));
      edges.forEach((e) => edgesDataSet.update({ id: e.id, color: { ...(e.color || {}) } } as any));
    });

    networkRef.current = network;

    // Frame the whole schema once the layout has been computed. Hierarchical mode runs
    // with physics disabled, so there's no stabilization event to fit on — without this
    // the graph opens at an arbitrary zoom/offset and the user has to press "Fit" to
    // see it. `once` so it never fights manual pan/zoom afterwards.
    network.once('afterDrawing', () => {
      try {
        network.fit({ animation: false });
      } catch {
        /* non-fatal: the Fit button still works */
      }
    });

    // Cleanup
    return () => {
      if (networkRef.current) {
        networkRef.current.destroy();
        networkRef.current = null;
      }
    };
  }, [schema, evaluation, layoutMode]);

  const handleAddNode = () => {
    setShowAddNodeDialog(true);
  };

  const handleAddNodeSubmit = (nodeData: {
    label: string;
    description: string;
    category?: string;
    properties: Array<{
      name: string;
      type: string;
      required: boolean;
      unique: boolean;
      indexed: boolean;
      description: string;
    }>;
  }) => {
    // Check if node already exists
    const nodeExists = editedSchema.nodes?.some(n => n.label === nodeData.label);
    if (nodeExists) {
      alert(`Node with label "${nodeData.label}" already exists in the schema.`);
      return;
    }

    // Create new node
    const newNode: NodeDefinition = {
      label: nodeData.label,
      description: nodeData.description,
      category: nodeData.category || 'Entity',
      properties: nodeData.properties.map(prop => ({
        name: prop.name,
        type: prop.type,
        required: prop.required,
        unique: prop.unique,
        indexed: prop.indexed,
        description: prop.description
      }))
    };

    // Update schema
    const updatedNodes = [...(editedSchema.nodes || []), newNode];
    const updated = {
      ...editedSchema,
      nodes: updatedNodes
    };

    setEditedSchema(updated);
    if (onSchemaUpdate) {
      onSchemaUpdate(updated);
    }

    // Close dialog
    setShowAddNodeDialog(false);
  };

  const handleAddRelationshipSubmit = (relationshipData: {
    sourceLabel: string;
    targetLabel: string;
    type: string;
    description: string;
    properties: Array<{
      name: string;
      type: string;
      required: boolean;
      description: string;
    }>;
  }) => {
    // Check if relationship already exists
    const relationshipExists = editedSchema.relationships?.some((r: any) => {
      const sourceLabel = r.from || r.sourceLabel;
      const targetLabel = r.to || r.targetLabel;
      return sourceLabel === relationshipData.sourceLabel &&
             r.type === relationshipData.type &&
             targetLabel === relationshipData.targetLabel;
    });

    if (relationshipExists) {
      alert(`Relationship "${relationshipData.sourceLabel} -[${relationshipData.type}]-> ${relationshipData.targetLabel}" already exists in the schema.`);
      return;
    }

    // Create new relationship - use backend format (from/to)
    const newRelationship: any = {
      from: relationshipData.sourceLabel,
      to: relationshipData.targetLabel,
      type: relationshipData.type,
      description: relationshipData.description,
      properties: relationshipData.properties.map(prop => ({
        name: prop.name,
        type: prop.type,
        required: prop.required,
        description: prop.description
      }))
    };

    // Update schema
    const updatedRelationships = [...(editedSchema.relationships || []), newRelationship];
    const updated = {
      ...editedSchema,
      relationships: updatedRelationships
    };

    setEditedSchema(updated);
    if (onSchemaUpdate) {
      onSchemaUpdate(updated);
    }

    // Close dialog
    setShowAddRelationshipDialog(false);
  };

  const handleDeleteNode = () => {
    if (!selectedNode) return;

    const updatedNodes = editedSchema.nodes?.filter(n => n.label !== selectedNode) || [];
    const updatedRelationships = editedSchema.relationships?.filter((r: any) => {
      const sourceLabel = r.from || r.sourceLabel;
      const targetLabel = r.to || r.targetLabel;
      return sourceLabel !== selectedNode && targetLabel !== selectedNode;
    }) || [];

    const updated = {
      ...editedSchema,
      nodes: updatedNodes,
      relationships: updatedRelationships
    };

    setEditedSchema(updated);
    if (onSchemaUpdate) {
      onSchemaUpdate(updated);
    }
    setSelectedNode(null);
  };

  const handleDeleteEdge = () => {
    if (!selectedEdge) return;

    const [source, type, target] = selectedEdge.split('_');
    const updatedRelationships = editedSchema.relationships?.filter((r: any) => {
      const sourceLabel = r.from || r.sourceLabel;
      const targetLabel = r.to || r.targetLabel;
      return !(sourceLabel === source && r.type === type && targetLabel === target);
    }) || [];

    const updated = {
      ...editedSchema,
      relationships: updatedRelationships
    };

    setEditedSchema(updated);
    if (onSchemaUpdate) {
      onSchemaUpdate(updated);
    }
    setSelectedEdge(null);
  };

  // Helper function to normalize properties from different formats
  const normalizeProperties = (properties: any): any[] => {
    if (!properties) return [];

    // If it's already an array, return it
    if (Array.isArray(properties)) {
      return properties;
    }

    // If it's an object (dictionary format like {propertyName: type})
    if (typeof properties === 'object') {
      const propsArray = Object.entries(properties).map(([name, type]) => ({
        name,
        type: typeof type === 'string' ? type : 'String',
        required: false,
        unique: false,
        indexed: false,
        description: `${name} property`
      }));
      return propsArray;
    }

    return [];
  };

  const getSelectedNodeDetails = () => {
    if (!selectedNode) return null;
    const node = schema.nodes?.find(n => n.label === selectedNode);

    // Normalize properties if node found
    if (node) {
      return {
        ...node,
        properties: normalizeProperties(node.properties)
      };
    }

    return node;
  };

  const getSelectedEdgeDetails = () => {
    if (!selectedEdge) return null;
    const [source, type, target] = selectedEdge.split('_');
    const edge = schema.relationships?.find((r: any) => {
      const sourceLabel = r.from || r.sourceLabel;
      const targetLabel = r.to || r.targetLabel;
      return sourceLabel === source && r.type === type && targetLabel === target;
    });
    // Normalize properties if edge found
    if (edge) {
      return {
        ...edge,
        properties: normalizeProperties(edge.properties)
      };
    }

    return edge;
  };

  // Start editing a node
  const handleStartEditNode = () => {
    const nodeDetails = getSelectedNodeDetails();
    if (nodeDetails) {
      setEditedNodeData({
        originalLabel: nodeDetails.label,
        label: nodeDetails.label,
        description: nodeDetails.description || '',
        category: nodeDetails.category || 'Entity',
        properties: [...nodeDetails.properties]
      });
      setIsEditingProperties(true);
    }
  };

  // Start editing a relationship
  const handleStartEditEdge = () => {
    const edgeDetails = getSelectedEdgeDetails();
    if (edgeDetails) {
      setEditedEdgeData({
        originalId: selectedEdge,
        from: edgeDetails.from || edgeDetails.sourceLabel,
        to: edgeDetails.to || edgeDetails.targetLabel,
        type: edgeDetails.type,
        description: edgeDetails.description || '',
        properties: [...edgeDetails.properties]
      });
      setIsEditingProperties(true);
    }
  };

  // Save node edits
  const handleSaveNodeEdits = () => {
    if (!editedNodeData) return;

    const updatedNodes = editedSchema.nodes?.map(n =>
      n.label === editedNodeData.originalLabel ? {
        ...n,
        label: editedNodeData.label,
        description: editedNodeData.description,
        category: editedNodeData.category,
        properties: editedNodeData.properties
      } : n
    ) || [];

    // Also update relationships that reference this node
    const updatedRelationships = editedSchema.relationships?.map((r: any) => {
      const sourceLabel = r.from || r.sourceLabel;
      const targetLabel = r.to || r.targetLabel;

      return {
        ...r,
        from: sourceLabel === editedNodeData.originalLabel ? editedNodeData.label : sourceLabel,
        to: targetLabel === editedNodeData.originalLabel ? editedNodeData.label : targetLabel
      };
    }) || [];

    const updated = {
      ...editedSchema,
      nodes: updatedNodes,
      relationships: updatedRelationships
    };

    setEditedSchema(updated);
    if (onSchemaUpdate) {
      onSchemaUpdate(updated);
    }

    setSelectedNode(editedNodeData.label);
    setEditedNodeData(null);
    setIsEditingProperties(false);
  };

  // Save relationship edits
  const handleSaveEdgeEdits = () => {
    if (!editedEdgeData) return;

    const [oldSource, oldType, oldTarget] = editedEdgeData.originalId.split('_');

    const updatedRelationships = editedSchema.relationships?.map((r: any) => {
      const sourceLabel = r.from || r.sourceLabel;
      const targetLabel = r.to || r.targetLabel;

      if (sourceLabel === oldSource && r.type === oldType && targetLabel === oldTarget) {
        return {
          ...r,
          from: editedEdgeData.from,
          to: editedEdgeData.to,
          type: editedEdgeData.type,
          description: editedEdgeData.description,
          properties: editedEdgeData.properties
        };
      }
      return r;
    }) || [];

    const updated = {
      ...editedSchema,
      relationships: updatedRelationships
    };

    setEditedSchema(updated);
    if (onSchemaUpdate) {
      onSchemaUpdate(updated);
    }

    setSelectedEdge(`${editedEdgeData.from}_${editedEdgeData.type}_${editedEdgeData.to}`);
    setEditedEdgeData(null);
    setIsEditingProperties(false);
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditedNodeData(null);
    setEditedEdgeData(null);
    setIsEditingProperties(false);
  };

  // Add property to node/edge being edited
  const handleAddPropertyToEdit = () => {
    if (editedNodeData) {
      setEditedNodeData({
        ...editedNodeData,
        properties: [
          ...editedNodeData.properties,
          { name: '', type: 'String', required: false, unique: false, indexed: false, description: '' }
        ]
      });
    } else if (editedEdgeData) {
      setEditedEdgeData({
        ...editedEdgeData,
        properties: [
          ...editedEdgeData.properties,
          { name: '', type: 'String', required: false, description: '' }
        ]
      });
    }
  };

  // Remove property from node/edge being edited
  const handleRemovePropertyFromEdit = (index: number) => {
    if (editedNodeData) {
      setEditedNodeData({
        ...editedNodeData,
        properties: editedNodeData.properties.filter((_: any, i: number) => i !== index)
      });
    } else if (editedEdgeData) {
      setEditedEdgeData({
        ...editedEdgeData,
        properties: editedEdgeData.properties.filter((_: any, i: number) => i !== index)
      });
    }
  };

  // Update property in node/edge being edited
  const handleUpdatePropertyInEdit = (index: number, field: string, value: any) => {
    if (editedNodeData) {
      const newProperties = [...editedNodeData.properties];
      newProperties[index] = {
        ...newProperties[index],
        [field]: value
      };
      setEditedNodeData({
        ...editedNodeData,
        properties: newProperties
      });
    } else if (editedEdgeData) {
      const newProperties = [...editedEdgeData.properties];
      newProperties[index] = {
        ...newProperties[index],
        [field]: value
      };
      setEditedEdgeData({
        ...editedEdgeData,
        properties: newProperties
      });
    }
  };

  return (
    <div ref={fullscreenRef} className={`flex gap-4 ${isFullscreen ? 'h-screen bg-white p-4' : 'h-[600px]'}`}>
      {/* Main Visualization Area */}
      <div className="flex-1 flex flex-col">
        <Card className="flex-1 p-4 overflow-hidden">
          {/* Toolbar */}
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold text-neutral-900">Schema Visualization</h3>
              {evaluation && (
                <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-800">
                  Evaluation highlights enabled
                </span>
              )}
              <div className="flex items-center gap-1 rounded-md border border-neutral-200 bg-white px-2 py-1">
                <Search className="h-3.5 w-3.5 text-neutral-400" />
                <input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSearch();
                  }}
                  placeholder="Find node…"
                  className="w-32 bg-transparent text-sm outline-none"
                />
              </div>
            </div>

            {/* View Controls */}
            <div className="flex items-center gap-1 bg-neutral-100 rounded-lg p-1">
              {/* Layout Toggle */}
              <Button
                variant={layoutMode === 'hierarchical' ? 'primary' : 'ghost'}
                size="sm"
                onClick={handleToggleLayoutMode}
                title="Hierarchical Layout (fixed positions)"
              >
                <Grid3X3 className="w-4 h-4" />
              </Button>
              <Button
                variant={layoutMode === 'freeform' ? 'primary' : 'ghost'}
                size="sm"
                onClick={handleToggleLayoutMode}
                title="Freeform Layout (drag nodes freely)"
              >
                <Move className="w-4 h-4" />
              </Button>

              <div className="w-px h-6 bg-neutral-300 mx-1" />

              {/* Zoom Controls */}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleZoomOut}
                title="Zoom Out"
              >
                <ZoomOut className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleFitToScreen}
                title="Fit to Screen"
              >
                Fit
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleZoomIn}
                title="Zoom In"
              >
                <ZoomIn className="w-4 h-4" />
              </Button>

              <div className="w-px h-6 bg-neutral-300 mx-1" />

              {/* Fullscreen */}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleToggleFullscreen}
                title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
              >
                {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </Button>

              <div className="w-px h-6 bg-neutral-300 mx-1" />

              {/* Export PNG */}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleExportPng}
                title="Export as PNG"
              >
                <Download className="w-4 h-4" />
              </Button>
            </div>

            {/* Edit Controls */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleAddNode}
                disabled={!isEditing}
              >
                <Plus className="w-4 h-4" />
                Add Node
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAddRelationshipDialog(true)}
                disabled={!isEditing || !schema.nodes || schema.nodes.length < 2}
              >
                <GitBranch className="w-4 h-4" />
                Add Relationship
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDeleteNode}
                disabled={!selectedNode || !isEditing}
              >
                <Trash2 className="w-4 h-4" />
                Delete Node
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDeleteEdge}
                disabled={!selectedEdge || !isEditing}
              >
                <Trash2 className="w-4 h-4" />
                Delete Edge
              </Button>
              {!isEditing ? (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setIsEditing(true)}
                >
                  <Edit2 className="w-4 h-4" />
                  Edit Mode
                </Button>
              ) : (
                <>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => {
                      setIsEditing(false);
                      if (onSchemaUpdate) {
                        onSchemaUpdate(editedSchema);
                      }
                    }}
                  >
                    <Save className="w-4 h-4" />
                    Save
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setIsEditing(false);
                      setEditedSchema(schema);
                    }}
                  >
                    <X className="w-4 h-4" />
                    Cancel
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Legend */}
          <div className="mb-4 space-y-2">
            {usedAreas.length > 0 && (
              <SchemaLegend usedAreas={usedAreas} hasCentralNodes={false} defaultExpanded={false} />
            )}
            <div className="flex items-center gap-6 text-xs text-neutral-600">
              {usedAreas.length === 0 && (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded border-2 border-sky-500 bg-sky-100"></div>
                  <span>Entity node</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded border-2 border-red-500 bg-red-50 border-dashed"></div>
                <span>Missing (from evaluation)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-0.5 bg-neutral-600"></div>
                <span>Relationship</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-0.5 bg-orange-500 border-dashed"></div>
                <span>Missing relationship</span>
              </div>
            </div>
          </div>

          {/* Vis.js Network Container */}
          <div
            ref={networkContainer}
            className="w-full flex-1 border border-neutral-200 rounded bg-white"
            style={{ height: 'calc(100% - 120px)' }}
          />
        </Card>
      </div>

      {/* Properties Panel */}
      <div className="w-80">
        <Card className="p-4 h-full overflow-auto">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-md font-semibold text-neutral-900">Properties</h3>
            {(selectedNode || selectedEdge) && !isEditingProperties && (
              <Button
                variant="outline"
                size="sm"
                onClick={selectedNode ? handleStartEditNode : handleStartEditEdge}
              >
                <Edit2 className="w-3 h-3" />
                Edit
              </Button>
            )}
          </div>

          {/* Node View Mode */}
          {selectedNode && !isEditingProperties && (() => {
            const nodeDetails = getSelectedNodeDetails();

            return (
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-neutral-700 mb-2">Node: {selectedNode}</h4>
                  {nodeDetails?.description && (
                    <p className="text-xs text-neutral-600 mb-3 p-2 bg-blue-50 rounded border border-blue-200">
                      {nodeDetails.description}
                    </p>
                  )}
                  <div className="text-xs text-neutral-600 space-y-2">
                    {nodeDetails?.properties && nodeDetails.properties.length > 0 ? (
                      nodeDetails.properties.map((prop: any, idx: number) => (
                        <div key={idx} className="p-2 bg-neutral-50 rounded border border-neutral-200">
                          <div className="font-medium">{prop.name}</div>
                          <div className="text-neutral-500">Type: {prop.type}</div>
                          {prop.required && <div className="text-blue-600 text-xs">✓ Required</div>}
                          {prop.unique && <div className="text-amber-600 text-xs">✓ Unique</div>}
                          {prop.indexed && <div className="text-green-600 text-xs">✓ Indexed</div>}
                          {prop.description && <div className="mt-1 text-neutral-600">{prop.description}</div>}
                        </div>
                      ))
                    ) : (
                      <div className="p-2 bg-neutral-50 rounded border border-neutral-200 text-neutral-500">
                        No properties defined
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Node Edit Mode */}
          {selectedNode && isEditingProperties && editedNodeData && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-neutral-700 mb-1">Node Label</label>
                <input
                  type="text"
                  value={editedNodeData.label}
                  onChange={(e) => setEditedNodeData({ ...editedNodeData, label: e.target.value })}
                  className="w-full px-2 py-1 text-sm border border-neutral-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-neutral-700 mb-1">Description</label>
                <textarea
                  value={editedNodeData.description}
                  onChange={(e) => setEditedNodeData({ ...editedNodeData, description: e.target.value })}
                  rows={2}
                  className="w-full px-2 py-1 text-sm border border-neutral-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-xs font-medium text-neutral-700">Properties</label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAddPropertyToEdit}
                  >
                    <Plus className="w-3 h-3" />
                    Add
                  </Button>
                </div>
                <div className="space-y-2">
                  {editedNodeData.properties.map((prop: any, idx: number) => (
                    <div key={idx} className="p-2 bg-neutral-50 rounded border border-neutral-200 space-y-2">
                      <div className="flex items-start gap-2">
                        <div className="flex-1">
                          <input
                            type="text"
                            value={prop.name}
                            onChange={(e) => handleUpdatePropertyInEdit(idx, 'name', e.target.value)}
                            placeholder="Property name"
                            className="w-full px-2 py-1 text-xs border border-neutral-300 rounded"
                          />
                        </div>
                        <button
                          onClick={() => handleRemovePropertyFromEdit(idx)}
                          className="p-1 hover:bg-red-100 rounded"
                        >
                          <Trash2 className="w-3 h-3 text-red-600" />
                        </button>
                      </div>
                      <select
                        value={prop.type}
                        onChange={(e) => handleUpdatePropertyInEdit(idx, 'type', e.target.value)}
                        className="w-full px-2 py-1 text-xs border border-neutral-300 rounded"
                      >
                        {PROPERTY_TYPES.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                      <input
                        type="text"
                        value={prop.description || ''}
                        onChange={(e) => handleUpdatePropertyInEdit(idx, 'description', e.target.value)}
                        placeholder="Description"
                        className="w-full px-2 py-1 text-xs border border-neutral-300 rounded"
                      />
                      <div className="flex items-center gap-3 text-xs">
                        <label className="flex items-center gap-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={prop.required}
                            onChange={(e) => handleUpdatePropertyInEdit(idx, 'required', e.target.checked)}
                            className="rounded border-neutral-300"
                          />
                          Required
                        </label>
                        <label className="flex items-center gap-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={prop.unique}
                            onChange={(e) => handleUpdatePropertyInEdit(idx, 'unique', e.target.checked)}
                            className="rounded border-neutral-300"
                          />
                          Unique
                        </label>
                        <label className="flex items-center gap-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={prop.indexed}
                            onChange={(e) => handleUpdatePropertyInEdit(idx, 'indexed', e.target.checked)}
                            className="rounded border-neutral-300"
                          />
                          Indexed
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleSaveNodeEdits}
                  className="flex-1"
                >
                  <Save className="w-3 h-3" />
                  Save
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancelEdit}
                  className="flex-1"
                >
                  <X className="w-3 h-3" />
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Edge View Mode */}
          {selectedEdge && !isEditingProperties && (
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-medium text-neutral-700 mb-2">Relationship: {getSelectedEdgeDetails()?.type}</h4>
                {getSelectedEdgeDetails()?.description && (
                  <p className="text-xs text-neutral-600 mb-3 p-2 bg-blue-50 rounded border border-blue-200">
                    {getSelectedEdgeDetails()?.description}
                  </p>
                )}
                <div className="text-xs text-neutral-600 space-y-2">
                  <div className="p-2 bg-neutral-50 rounded border border-neutral-200">
                    <div>From: <span className="font-medium">{(getSelectedEdgeDetails() as any)?.from || (getSelectedEdgeDetails() as any)?.sourceLabel}</span></div>
                    <div>To: <span className="font-medium">{(getSelectedEdgeDetails() as any)?.to || (getSelectedEdgeDetails() as any)?.targetLabel}</span></div>
                  </div>

                  {getSelectedEdgeDetails()?.properties && getSelectedEdgeDetails()!.properties.length > 0 ? (
                    <>
                      <div className="text-xs font-semibold text-neutral-700 mt-3">Properties:</div>
                      {getSelectedEdgeDetails()!.properties.map((prop: any, idx: number) => (
                        <div key={idx} className="p-2 bg-neutral-50 rounded border border-neutral-200">
                          <div className="font-medium">{prop.name}</div>
                          <div className="text-neutral-500">Type: {prop.type}</div>
                          {prop.required && <div className="text-blue-600 text-xs">✓ Required</div>}
                          {prop.description && <div className="mt-1 text-neutral-600">{prop.description}</div>}
                        </div>
                      ))}
                    </>
                  ) : (
                    <div className="p-2 bg-neutral-50 rounded border border-neutral-200 text-neutral-500">
                      No properties defined
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Edge Edit Mode */}
          {selectedEdge && isEditingProperties && editedEdgeData && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-neutral-700 mb-1">From Node</label>
                <select
                  value={editedEdgeData.from}
                  onChange={(e) => setEditedEdgeData({ ...editedEdgeData, from: e.target.value })}
                  className="w-full px-2 py-1 text-sm border border-neutral-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  {schema.nodes?.map(node => (
                    <option key={node.label} value={node.label}>{node.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-neutral-700 mb-1">Relationship Type</label>
                <input
                  type="text"
                  value={editedEdgeData.type}
                  onChange={(e) => setEditedEdgeData({ ...editedEdgeData, type: e.target.value.toUpperCase() })}
                  className="w-full px-2 py-1 text-sm border border-neutral-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-neutral-700 mb-1">To Node</label>
                <select
                  value={editedEdgeData.to}
                  onChange={(e) => setEditedEdgeData({ ...editedEdgeData, to: e.target.value })}
                  className="w-full px-2 py-1 text-sm border border-neutral-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  {schema.nodes?.map(node => (
                    <option key={node.label} value={node.label}>{node.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-neutral-700 mb-1">Description</label>
                <textarea
                  value={editedEdgeData.description}
                  onChange={(e) => setEditedEdgeData({ ...editedEdgeData, description: e.target.value })}
                  rows={2}
                  className="w-full px-2 py-1 text-sm border border-neutral-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-xs font-medium text-neutral-700">Properties</label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAddPropertyToEdit}
                  >
                    <Plus className="w-3 h-3" />
                    Add
                  </Button>
                </div>
                <div className="space-y-2">
                  {editedEdgeData.properties.map((prop: any, idx: number) => (
                    <div key={idx} className="p-2 bg-neutral-50 rounded border border-neutral-200 space-y-2">
                      <div className="flex items-start gap-2">
                        <div className="flex-1">
                          <input
                            type="text"
                            value={prop.name}
                            onChange={(e) => handleUpdatePropertyInEdit(idx, 'name', e.target.value)}
                            placeholder="Property name"
                            className="w-full px-2 py-1 text-xs border border-neutral-300 rounded"
                          />
                        </div>
                        <button
                          onClick={() => handleRemovePropertyFromEdit(idx)}
                          className="p-1 hover:bg-red-100 rounded"
                        >
                          <Trash2 className="w-3 h-3 text-red-600" />
                        </button>
                      </div>
                      <select
                        value={prop.type}
                        onChange={(e) => handleUpdatePropertyInEdit(idx, 'type', e.target.value)}
                        className="w-full px-2 py-1 text-xs border border-neutral-300 rounded"
                      >
                        {PROPERTY_TYPES.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                      <input
                        type="text"
                        value={prop.description || ''}
                        onChange={(e) => handleUpdatePropertyInEdit(idx, 'description', e.target.value)}
                        placeholder="Description"
                        className="w-full px-2 py-1 text-xs border border-neutral-300 rounded"
                      />
                      <div className="flex items-center gap-3 text-xs">
                        <label className="flex items-center gap-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={prop.required}
                            onChange={(e) => handleUpdatePropertyInEdit(idx, 'required', e.target.checked)}
                            className="rounded border-neutral-300"
                          />
                          Required
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleSaveEdgeEdits}
                  className="flex-1"
                >
                  <Save className="w-3 h-3" />
                  Save
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancelEdit}
                  className="flex-1"
                >
                  <X className="w-3 h-3" />
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {!selectedNode && !selectedEdge && (
            <div className="text-sm text-neutral-500 text-center py-8">
              Select a node or relationship to view properties
            </div>
          )}
        </Card>
      </div>

      {/* Add Node Dialog */}
      <AddNodeDialog
        isOpen={showAddNodeDialog}
        onClose={() => setShowAddNodeDialog(false)}
        onAdd={handleAddNodeSubmit}
      />

      {/* Add Relationship Dialog */}
      <AddRelationshipDialog
        isOpen={showAddRelationshipDialog}
        onClose={() => setShowAddRelationshipDialog(false)}
        nodes={schema.nodes || []}
        onAdd={handleAddRelationshipSubmit}
      />
    </div>
  );
};
